/**
 * Modal flutuante da OS — o atalho do quadro kanban.
 *
 * A tela de detalhe (`/os/:n`) continua existindo e é a fonte completa. Este
 * modal cobre o que o operador faz o dia inteiro sem querer perder o quadro de
 * vista: conferir a OS inteira, chamar o cliente no WhatsApp e mover de etapa.
 *
 * Por que abas, e não uma folha longa: a resposta de `/ordens-servico/:n` traz
 * sete blocos (aparelho, defeitos, serviços, peças, pagamentos, gastos,
 * histórico). Empilhados viravam dois metros de rolagem para achar o que
 * interessa; separados, cada leitura cabe na tela sem esconder nada. A
 * requisição continua sendo UMA — trocar de aba não vai à rede. A única busca
 * extra é o histórico do cliente, e ela só dispara se a aba "Cliente" for
 * aberta.
 *
 * As regras condicionais de status são as MESMAS do `StatusOs`, e pelo mesmo
 * motivo: `exigeSaida` e `exigePagamento` vêm do catálogo, não de `if` na tela.
 * O arrastar-e-soltar do quadro solta aqui quando a etapa de destino pede
 * pagamento — mover sem registrar o valor deixaria o caixa mentindo.
 *
 * Movimento com GSAP, sempre com a saída de emergência do `prefers-reduced-
 * motion`: quem pediu menos animação recebe o estado final direto.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import {
  FORMAS_PAGAMENTO,
  acharStatus,
  aplicarMascaraMoeda,
  formatarBRL,
  mascaraParaCentavos,
  type FormaPagamento,
  type ItemOrcamento,
  type OsDetalhe,
} from '@tcm/shared';
import { useHistoricoCliente, useMoverStatus, useOs, useStatusCatalogo } from '../features/queries';
import { cx, Esqueleto } from './ui';
import { ErroNaTela } from './layout';
import { Contador, movimentoReduzido } from './animados';
import {
  descreverDias,
  formatarData,
  formatarDataHora,
  formatarTelefoneBR,
  normalizarTelefone,
} from '../lib/format';
import { tomStatus, type TomStatus } from '../lib/paleta';
import { regrasDePagamento } from '../lib/pagamento';

type Aba = 'resumo' | 'orcamento' | 'historico' | 'cliente';

export function ModalOs({
  osNumber,
  statusInicial,
  aoFechar,
}: {
  osNumber: number;
  /** Etapa pré-selecionada — vem do cartão solto numa coluna que exige pagamento. */
  statusInicial?: string | undefined;
  aoFechar: () => void;
}) {
  const { data, isLoading, error, refetch } = useOs(osNumber);
  const catalogo = useStatusCatalogo();
  const mover = useMoverStatus();

  const fundo = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const corpo = useRef<HTMLDivElement>(null);

  const [aba, setAba] = useState<Aba>('resumo');
  // Chegou pelo arrastar-e-soltar: a gaveta de mover já abre pronta, senão o
  // operador teria de procurar o que ele acabou de pedir com o gesto.
  const [moverAberto, setMoverAberto] = useState(Boolean(statusInicial));

  const [status, setStatus] = useState(statusInicial ?? '');
  const [saida, setSaida] = useState('');
  const [valorTexto, setValorTexto] = useState('');
  const [forma, setForma] = useState<FormaPagamento>('PIX');
  const [parcelas, setParcelas] = useState(1);

  /* -------------------- animação de entrada e saída -------------------- */

  const { contextSafe } = useGSAP(
    () => {
      if (movimentoReduzido()) return;
      gsap
        .timeline()
        .from(fundo.current, { opacity: 0, duration: 0.2, ease: 'none' })
        .from(
          painel.current,
          { opacity: 0, y: 28, scale: 0.96, duration: 0.42, ease: 'power3.out' },
          0,
        );
    },
    { scope: fundo },
  );

  // Fechar tem de esperar a animação; sem a trava, dois cliques disparariam
  // duas saídas e o `aoFechar` rodaria no meio da primeira.
  const fechando = useRef(false);
  const fechar = contextSafe(() => {
    if (fechando.current) return;
    fechando.current = true;

    if (movimentoReduzido()) {
      aoFechar();
      return;
    }
    gsap.to(painel.current, { opacity: 0, y: 14, scale: 0.98, duration: 0.2, ease: 'power2.in' });
    gsap.to(fundo.current, { opacity: 0, duration: 0.22, onComplete: aoFechar });
  });

  // O listener de Esc é montado uma vez só; `fechar` muda de identidade a cada
  // render (é `contextSafe`), então quem o efeito enxerga é o ref.
  const fecharRef = useRef(fechar);
  fecharRef.current = fechar;

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') fecharRef.current();
    };
    document.addEventListener('keydown', aoTeclar);

    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    painel.current?.focus();

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = overflowAnterior;
    };
  }, []);

  /* ---------------------------- dados ---------------------------- */

  // Sem etapa pré-escolhida, o seletor começa no status atual da OS.
  useEffect(() => {
    if (!data) return;
    setStatus((atual) => atual || data.os.status);
    setSaida((atual) => atual || (data.os.saida ? data.os.saida.slice(0, 10) : ''));
  }, [data]);

  const os = data?.os;
  // Vazio, e não `null`: as abas tratam "sem telefone" como um caso normal.
  const telefone = normalizarTelefone(os?.telefone) ?? '';
  const tom = tomStatus(os?.status);

  const regra = acharStatus(status);
  const opcoes = catalogo.data?.data ?? [];
  const valorCentavos = mascaraParaCentavos(valorTexto);
  const mudouEtapa = data != null && status !== '' && status !== data.os.status;

  // `exigePagamento` não quer dizer "cobre de novo" — ver `lib/pagamento.ts`.
  // Sem `data` a gaveta nem abre; o zerado aqui só existe para o primeiro
  // render, antes da OS chegar.
  const pagamento = regrasDePagamento(
    data?.financeiro ?? {
      totalServicosCentavos: 0,
      totalPecasCentavos: 0,
      valorOrcadoCentavos: 0,
      totalPagoCentavos: 0,
      totalGastosCentavos: 0,
      lucroCentavos: 0,
      saldoDevedorCentavos: 0,
    },
    regra.exigePagamento,
    valorCentavos,
  );

  const podeMover =
    mudouEtapa &&
    (!regra.exigeSaida || saida !== '') &&
    (!pagamento.obrigatorio || valorCentavos > 0);

  const contagens = useMemo(
    () => ({
      orcamento:
        (data?.servicos.length ?? 0) +
        (data?.pecas.length ?? 0) +
        (data?.pagamentos.length ?? 0) +
        (data?.gastos.length ?? 0),
      historico: data?.historico.length ?? 0,
    }),
    [data],
  );

  async function confirmar(): Promise<void> {
    await mover.mutateAsync({
      osNumber,
      status,
      saida: regra.exigeSaida && saida ? new Date(`${saida}T12:00:00`).toISOString() : null,
      pagamento:
        regra.exigePagamento && valorCentavos > 0
          ? {
              valorCentavos,
              forma,
              ...(forma === 'Cartão de Crédito' ? { parcelas } : {}),
            }
          : null,
    });
    fechar();
  }

  /* ------------------------- troca de aba ------------------------- */

  useGSAP(
    () => {
      // Rolagem volta ao topo: manter a posição da aba anterior faz a nova
      // abrir no meio de um bloco qualquer.
      corpo.current?.scrollTo({ top: 0 });
      if (movimentoReduzido()) return;

      const blocos = corpo.current?.children;
      if (!blocos || blocos.length === 0) return;
      gsap.fromTo(
        blocos,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.32, stagger: 0.045, ease: 'power2.out', overwrite: true },
      );
    },
    { dependencies: [aba, data != null], scope: corpo },
  );

  return createPortal(
    <div
      ref={fundo}
      // Sem `p-4` no celular: a ficha vira uma folha que ocupa a tela inteira,
      // como um app nativo — a moldura de 1 rem só roubava altura útil de uma
      // tela que já é curta. A margem volta a partir de `sm`.
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-stone-900/40 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={fechar}
    >
      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-label={`Detalhes da OS ${osNumber}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={cx(
          'flex w-full flex-col overflow-hidden bg-creme-50 shadow-2xl ring-1 ring-creme-300 focus:outline-none',
          // Celular: folha inteira, sem cantos arredondados brigando com a
          // borda da tela. `dvh` e não `vh` — o `vh` conta a barra de endereço
          // recolhida, e o rodapé de ações ficava embaixo dela.
          // `h-dvh` com `box-border`: o recuo da área segura entra DENTRO da
          // altura, então a folha nunca passa da tela e o fundo não ganha uma
          // rolagem extra por causa do notch.
          'h-dvh area-segura-topo area-segura-base',
          // Desktop: a caixa centrada de sempre.
          'sm:my-auto sm:h-auto sm:max-h-[88dvh] sm:max-w-4xl sm:rounded-2xl',
        )}
      >
        {/* Filete no tom da etapa: identifica a OS antes de qualquer leitura. */}
        <div className="h-0.5 shrink-0" style={{ backgroundColor: tom.forte }} />

        <Topo
          osNumber={osNumber}
          os={os}
          tom={tom}
          telefone={telefone}
          diasEmAberto={data?.os.diasEmAberto}
          aoFechar={fechar}
        />

        <Abas
          aba={aba}
          aoTrocar={setAba}
          contagens={contagens}
          desabilitado={!data}
        />

        <div ref={corpo} className="scrollbar-creme flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
          {isLoading && <Esqueleto linhas={8} />}
          {error && <ErroNaTela erro={error} aoTentar={() => void refetch()} />}

          {data && aba === 'resumo' && <AbaResumo dados={data} tom={tom} />}
          {data && aba === 'orcamento' && <AbaOrcamento dados={data} />}
          {data && aba === 'historico' && <AbaHistorico dados={data} />}
          {data && aba === 'cliente' && (
            <AbaCliente
              // A busca só existe enquanto esta aba está montada — é a única
              // requisição além da OS, e ela é opcional por construção.
              telefone={telefone}
              nome={`${data.os.nome} ${data.os.sobrenome}`.trim()}
              osAtual={osNumber}
            />
          )}
        </div>

        {/* ------------------- gaveta de mover etapa ------------------- */}
        <Gaveta aberta={moverAberto}>
          <div className="border-t border-creme-200 bg-creme-100 px-4 py-4 sm:px-6">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-medium text-stone-500">Nova etapa</span>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  aria-label="Nova etapa"
                  className="mt-1 min-h-11 w-full rounded-lg border-0 bg-white px-3 py-2 text-sm text-stone-800 ring-1 ring-creme-300 focus:ring-2 focus:ring-brand-500"
                >
                  {/* Status fora do catálogo continua visível: o seletor nunca fica em branco. */}
                  {!opcoes.some((o) => o.codigo === status) && status && (
                    <option value={status}>{acharStatus(status).rotulo}</option>
                  )}
                  {opcoes.map((o) => (
                    <option key={o.codigo} value={o.codigo}>
                      {o.rotulo}
                    </option>
                  ))}
                </select>
              </label>

              {mudouEtapa && regra.exigeSaida && (
                <label className="block">
                  <span className="text-xs font-medium text-stone-500">
                    Data de saída (obrigatória)
                  </span>
                  <input
                    type="date"
                    value={saida}
                    onChange={(e) => setSaida(e.target.value)}
                    className="mt-1 min-h-11 w-full rounded-lg border-0 bg-white px-3 py-2 text-sm text-stone-800 ring-1 ring-creme-300 focus:ring-2 focus:ring-brand-500"
                  />
                </label>
              )}

              {mudouEtapa && regra.exigePagamento && (
                <>
                  <label className="block">
                    <span className="text-xs font-medium text-stone-500">
                      {pagamento.obrigatorio
                        ? 'Valor pago (obrigatório)'
                        : `Valor pago (opcional — já recebido ${formatarBRL(pagamento.jaRecebido)})`}
                    </span>
                    <input
                      inputMode="numeric"
                      placeholder="0,00"
                      value={valorTexto}
                      onChange={(e) => setValorTexto(aplicarMascaraMoeda(e.target.value))}
                      className="mt-1 min-h-11 w-full rounded-lg border-0 bg-white px-3 py-2 text-sm tabular-nums text-stone-800 ring-1 ring-creme-300 focus:ring-2 focus:ring-brand-500"
                    />
                  </label>

                  <label className="block">
                    <span className="text-xs font-medium text-stone-500">Forma</span>
                    <select
                      value={forma}
                      onChange={(e) => setForma(e.target.value as FormaPagamento)}
                      className="mt-1 min-h-11 w-full rounded-lg border-0 bg-white px-3 py-2 text-sm text-stone-800 ring-1 ring-creme-300 focus:ring-2 focus:ring-brand-500"
                    >
                      {FORMAS_PAGAMENTO.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </label>

                  {forma === 'Cartão de Crédito' && (
                    <label className="block">
                      <span className="text-xs font-medium text-stone-500">Parcelas</span>
                      <select
                        value={parcelas}
                        onChange={(e) => setParcelas(Number(e.target.value))}
                        className="mt-1 min-h-11 w-full rounded-lg border-0 bg-white px-3 py-2 text-sm text-stone-800 ring-1 ring-creme-300 focus:ring-2 focus:ring-brand-500"
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>
                            {n}x
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  {data && data.financeiro.saldoDevedorCentavos > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setValorTexto(
                          aplicarMascaraMoeda(String(data.financeiro.saldoDevedorCentavos)),
                        )
                      }
                      className="self-end text-left text-xs font-semibold text-brand-700 hover:underline"
                    >
                      Usar o saldo devedor ({formatarBRL(data.financeiro.saldoDevedorCentavos)})
                    </button>
                  )}

                  {/* Avisa, não trava: acréscimo combinado no balcão e fora do
                      orçamento é caso real. O que não pode é o valor dobrar
                      sem ninguém ver. */}
                  {pagamento.excedente > 0 && (
                    <p
                      role="alert"
                      className="sm:col-span-2 rounded-lg bg-mostarda/10 px-3 py-2 text-xs font-medium text-mostarda"
                    >
                      Com este valor o total pago fica {formatarBRL(pagamento.excedente)} acima do
                      orçado
                      {pagamento.jaRecebido > 0 && (
                        <> — a OS já recebeu {formatarBRL(pagamento.jaRecebido)}</>
                      )}
                      .
                    </p>
                  )}
                </>
              )}
            </div>

            {mover.error && (
              <div className="mt-3">
                <ErroNaTela erro={mover.error} />
              </div>
            )}
          </div>
        </Gaveta>

        {/* ---------------------------- rodapé ---------------------------- */}
        {/* No celular o rodapé empilha: os atalhos de navegação em cima e as
            ações de etapa embaixo, cada bloco com a linha inteira. Lado a lado
            os cinco alvos não cabiam em 375 px sem virar texto de 11 px. */}
        <footer className="flex shrink-0 flex-col gap-2 border-t border-creme-200 bg-creme-100 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-6">
          <div className="flex flex-wrap items-center gap-1">
            <Link
              to="/os/$osNumber/orcamento"
              params={{ osNumber: String(osNumber) }}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-stone-500 transition-colors hover:bg-creme-100 hover:text-stone-900 active:bg-creme-200"
            >
              Orçamento
            </Link>
            <Link
              to="/os/$osNumber"
              params={{ osNumber: String(osNumber) }}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-stone-500 transition-colors hover:bg-creme-100 hover:text-stone-900 active:bg-creme-200"
            >
              OS completa
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMoverAberto((v) => !v)}
              aria-expanded={moverAberto}
              className="min-h-11 flex-1 rounded-lg px-3 py-2 text-sm font-semibold text-stone-600 ring-1 ring-creme-300 transition-colors hover:bg-creme-100 hover:text-stone-900 active:bg-creme-200 sm:flex-none"
            >
              {moverAberto ? 'Cancelar' : 'Mover etapa'}
            </button>

            {moverAberto && (
              <button
                type="button"
                disabled={!podeMover || mover.isPending}
                onClick={() => void confirmar()}
                className={cx(
                  'min-h-11 flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition-colors sm:flex-none',
                  podeMover && !mover.isPending
                    ? 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-700'
                    : 'cursor-not-allowed bg-creme-100 text-stone-400',
                )}
              >
                {mover.isPending
                  ? 'Movendo…'
                  : mudouEtapa
                    ? `Mover para "${regra.rotulo}"`
                    : 'Escolha outra etapa'}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/* Cabeçalho                                                           */
/* ------------------------------------------------------------------ */

function Topo({
  osNumber,
  os,
  tom,
  telefone,
  diasEmAberto,
  aoFechar,
}: {
  osNumber: number;
  os: OsDetalhe['os'] | undefined;
  tom: TomStatus;
  telefone: string;
  diasEmAberto: number | null | undefined;
  aoFechar: () => void;
}) {
  const cliente = os ? `${os.nome} ${os.sobrenome}`.trim() : '';

  return (
    <header className="flex shrink-0 items-start justify-between gap-3 border-b border-creme-200 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4">
      <div className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-bold uppercase"
          style={{ backgroundColor: tom.suave, color: tom.texto }}
        >
          {iniciais(cliente)}
        </span>

        <div className="min-w-0">
          <h2 className="truncate text-lg font-bold text-stone-800">
            {cliente || `OS #${osNumber}`}
          </h2>

          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-stone-400">
            <span className="font-bold tabular-nums text-stone-500">OS #{osNumber}</span>
            {os && (
              <>
                <span aria-hidden="true">·</span>
                <span
                  className="rounded px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide"
                  style={{ backgroundColor: tom.suave, color: tom.texto }}
                >
                  {os.statusRotulo}
                </span>
                {descreverDias(diasEmAberto) && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{descreverDias(diasEmAberto)} em aberto</span>
                  </>
                )}
              </>
            )}
          </div>

          {os && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {telefone ? (
                <>
                  <span className="rounded-full bg-creme-100 px-2 py-0.5 text-[11px] font-medium tabular-nums text-stone-600">
                    {formatarTelefoneBR(os.telefone)}
                  </span>
                  <a
                    href={`https://wa.me/${telefone}`}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full bg-oliva/10 px-2 py-0.5 text-[11px] font-semibold text-oliva transition-colors hover:bg-oliva/20"
                  >
                    WhatsApp
                  </a>
                  <a
                    href={`tel:+${telefone}`}
                    className="rounded-full bg-creme-200 px-2 py-0.5 text-[11px] font-semibold text-stone-600 transition-colors hover:bg-creme-300"
                  >
                    Ligar
                  </a>
                </>
              ) : (
                <span className="text-[11px] text-stone-400">Sem telefone cadastrado</span>
              )}
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={aoFechar}
        aria-label="Fechar"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-stone-500 transition-colors hover:bg-creme-100 hover:text-stone-900 active:bg-creme-200 sm:h-9 sm:w-9"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="m6 6 12 12M18 6 6 18" />
        </svg>
      </button>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Abas                                                                */
/* ------------------------------------------------------------------ */

function Abas({
  aba,
  aoTrocar,
  contagens,
  desabilitado,
}: {
  aba: Aba;
  aoTrocar: (a: Aba) => void;
  contagens: { orcamento: number; historico: number };
  desabilitado: boolean;
}) {
  const barra = useRef<HTMLDivElement>(null);
  const trilho = useRef<HTMLDivElement>(null);

  const itens: Array<{ id: Aba; rotulo: string; contagem?: number }> = [
    { id: 'resumo', rotulo: 'Resumo' },
    { id: 'orcamento', rotulo: 'Orçamento', contagem: contagens.orcamento },
    { id: 'historico', rotulo: 'Histórico', contagem: contagens.historico },
    { id: 'cliente', rotulo: 'Cliente' },
  ];

  // O sublinhado desliza entre as abas em vez de piscar de posição — é o que
  // dá continuidade ao gesto de trocar de aba.
  useGSAP(
    () => {
      const ativo = trilho.current?.querySelector<HTMLElement>('[aria-selected="true"]');
      if (!ativo || !barra.current) return;

      const destino = { x: ativo.offsetLeft, width: ativo.offsetWidth };
      if (movimentoReduzido()) gsap.set(barra.current, destino);
      else gsap.to(barra.current, { ...destino, duration: 0.35, ease: 'power3.out' });
    },
    { dependencies: [aba], scope: trilho },
  );

  return (
    <div
      ref={trilho}
      role="tablist"
      aria-label="Seções da OS"
      // As quatro abas somam mais de 375 px com os contadores acesos: em vez de
      // espremer a fonte, a barra rola de lado. `scrollbar-none` porque no
      // celular a barra de rolagem só cobriria o sublinhado da aba ativa.
      className="relative flex shrink-0 gap-1 overflow-x-auto border-b border-creme-200 px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:px-4"
    >
      {itens.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={aba === item.id}
          disabled={desabilitado}
          onClick={() => aoTrocar(item.id)}
          className={cx(
            'relative flex shrink-0 items-center gap-1.5 px-3 py-3 text-sm font-semibold whitespace-nowrap transition-colors disabled:opacity-40 sm:py-2.5',
            aba === item.id ? 'text-stone-800' : 'text-stone-400 hover:text-stone-700',
          )}
        >
          {item.rotulo}
          {item.contagem != null && item.contagem > 0 && (
            <span className="rounded-full bg-creme-100 px-1.5 text-[10px] font-bold tabular-nums text-stone-500">
              {item.contagem}
            </span>
          )}
        </button>
      ))}

      <div
        ref={barra}
        aria-hidden="true"
        className="absolute bottom-0 left-0 h-0.5 w-0 rounded-full bg-brand-600"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Aba: resumo                                                         */
/* ------------------------------------------------------------------ */

function AbaResumo({ dados, tom }: { dados: OsDetalhe; tom: TomStatus }) {
  const { os, financeiro } = dados;
  const aparelho = [os.aparelho, os.marca, os.modelo].filter(Boolean).join(' · ');

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Painel rotulo="Orçado" centavos={financeiro.valorOrcadoCentavos} classe="text-stone-800" />
        <Painel rotulo="Pago" centavos={financeiro.totalPagoCentavos} classe="text-oliva" />
        <Painel
          rotulo="Falta receber"
          centavos={financeiro.saldoDevedorCentavos}
          classe={financeiro.saldoDevedorCentavos > 0 ? 'text-mostarda' : 'text-stone-400'}
        />
        <Painel
          rotulo="Lucro"
          centavos={financeiro.lucroCentavos}
          classe={financeiro.lucroCentavos >= 0 ? 'text-petroleo' : 'text-terracota'}
        />
      </div>

      <dl className="mb-4 grid gap-x-4 gap-y-3 rounded-xl bg-white p-4 ring-1 ring-creme-200 sm:grid-cols-2 lg:grid-cols-3">
        <Linha rotulo="Aparelho" valor={aparelho || '—'} />
        <Linha rotulo="Técnico" valor={os.tecnicoNome ?? 'Não atribuído'} />
        <Linha
          rotulo="Etapa atual"
          valor={
            <span className="font-semibold" style={{ color: tom.texto }}>
              {os.statusRotulo}
            </span>
          }
        />
        <Linha rotulo="Entrada" valor={formatarDataHora(os.entrada) || '—'} />
        <Linha rotulo="Saída" valor={os.saida ? formatarData(os.saida) : 'Pendente'} />
        <Linha rotulo="Em aberto há" valor={descreverDias(os.diasEmAberto) || '—'} />
      </dl>

      <Secao titulo="Defeito relatado pelo cliente">
        <p className="whitespace-pre-wrap text-sm text-stone-600">{os.defeitoRelatado || '—'}</p>
      </Secao>

      <Secao titulo={`Defeitos identificados (${dados.defeitos.length})`}>
        {dados.defeitos.length === 0 ? (
          <p className="text-sm text-stone-400">Nenhum diagnóstico lançado.</p>
        ) : (
          <ul className="list-disc space-y-1 pl-5 text-sm text-stone-600">
            {dados.defeitos.map((d) => (
              <li key={d.id}>{d.descricao}</li>
            ))}
          </ul>
        )}
      </Secao>

      {os.observacoes && (
        <Secao titulo="Observações">
          <p className="whitespace-pre-wrap text-sm text-stone-600">{os.observacoes}</p>
        </Secao>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Aba: orçamento e dinheiro                                           */
/* ------------------------------------------------------------------ */

function AbaOrcamento({ dados }: { dados: OsDetalhe }) {
  const { financeiro } = dados;

  return (
    <>
      <Secao titulo="Serviços">
        <ListaItens itens={dados.servicos} vazio="Nenhum serviço lançado." />
      </Secao>

      <Secao titulo="Peças">
        <ListaItens itens={dados.pecas} vazio="Nenhuma peça lançada." />
      </Secao>

      <Secao titulo="Pagamentos recebidos">
        {dados.pagamentos.length === 0 ? (
          <p className="text-sm text-stone-400">Nenhum pagamento registrado.</p>
        ) : (
          <ul className="divide-y divide-creme-200 text-sm">
            {dados.pagamentos.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-1.5">
                <span className="truncate text-stone-600">{p.forma ?? 'Forma não informada'}</span>
                <span className="shrink-0 font-semibold tabular-nums text-oliva">
                  {formatarBRL(p.valorCentavos)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Secao>

      <Secao titulo="Gastos lançados no caixa">
        {dados.gastos.length === 0 ? (
          <p className="text-sm text-stone-400">Nenhum gasto lançado.</p>
        ) : (
          <ul className="divide-y divide-creme-200 text-sm">
            {dados.gastos.map((g) => (
              <li key={g.id} className="flex items-center justify-between gap-3 py-1.5">
                <span className="min-w-0 flex-1 truncate text-stone-600">{g.descricao}</span>
                <span className="shrink-0 text-xs tabular-nums text-stone-400">
                  {formatarData(g.createdAt) || '—'}
                </span>
                {/* Exibido em módulo: o sinal negativo é convenção interna do caixa. */}
                <span className="shrink-0 font-semibold tabular-nums text-terracota">
                  {formatarBRL(Math.abs(g.valorCentavos))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Secao>

      <Secao titulo="Fechamento">
        <dl className="space-y-1.5 text-sm">
          <Conta rotulo="Serviços" centavos={financeiro.totalServicosCentavos} />
          <Conta rotulo="Peças" centavos={financeiro.totalPecasCentavos} />
          <Conta rotulo="Orçado" centavos={financeiro.valorOrcadoCentavos} forte />
          <Conta rotulo="Pago" centavos={financeiro.totalPagoCentavos} classe="text-oliva" />
          <Conta
            rotulo="Gastos"
            centavos={Math.abs(financeiro.totalGastosCentavos)}
            classe="text-terracota"
          />
          <Conta
            rotulo="Falta receber"
            centavos={financeiro.saldoDevedorCentavos}
            classe={financeiro.saldoDevedorCentavos > 0 ? 'text-mostarda' : 'text-stone-400'}
          />
          <Conta
            rotulo="Lucro"
            centavos={financeiro.lucroCentavos}
            classe={financeiro.lucroCentavos >= 0 ? 'text-petroleo' : 'text-terracota'}
            forte
          />
        </dl>
      </Secao>
    </>
  );
}

function ListaItens({ itens, vazio }: { itens: ItemOrcamento[]; vazio: string }) {
  if (itens.length === 0) return <p className="text-sm text-stone-400">{vazio}</p>;

  const total = itens.reduce((acc, i) => acc + i.valorCentavos, 0);

  return (
    <ul className="divide-y divide-creme-200 text-sm">
      {itens.map((item) => (
        <li key={item.id} className="flex items-center justify-between gap-3 py-1.5">
          <span className="min-w-0 flex-1 truncate text-stone-600">{item.descricao}</span>
          <span className="shrink-0 tabular-nums text-stone-500">
            {formatarBRL(item.valorCentavos)}
          </span>
        </li>
      ))}
      <li className="flex items-center justify-between gap-3 py-1.5 font-semibold">
        <span className="text-stone-500">Total</span>
        <span className="tabular-nums text-stone-800">{formatarBRL(total)}</span>
      </li>
    </ul>
  );
}

/* ------------------------------------------------------------------ */
/* Aba: histórico de etapas                                            */
/* ------------------------------------------------------------------ */

function AbaHistorico({ dados }: { dados: OsDetalhe }) {
  if (dados.historico.length === 0) {
    return <p className="py-8 text-center text-sm text-stone-400">Nenhuma mudança registrada.</p>;
  }

  return (
    <ol className="relative space-y-4 border-l border-creme-200 pl-5">
      {dados.historico.map((evento) => {
        const tom = tomStatus(evento.statusNovo);
        return (
          <li key={evento.id} className="relative">
            <span
              aria-hidden="true"
              className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: tom.forte }}
            />
            <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
              <span className="font-semibold" style={{ color: tom.texto }}>
                {evento.statusNovoRotulo}
              </span>
              {evento.statusAnterior && (
                <span className="text-xs text-stone-400">
                  veio de {acharStatus(evento.statusAnterior).rotulo}
                </span>
              )}
            </p>
            <p className="mt-0.5 text-xs text-stone-400">
              {formatarDataHora(evento.createdAt) || '—'}
              {evento.usuarioNome && ` · ${evento.usuarioNome}`}
            </p>
            {evento.observacao && (
              <p className="mt-1 whitespace-pre-wrap text-xs text-stone-500">{evento.observacao}</p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ------------------------------------------------------------------ */
/* Aba: cliente                                                        */
/* ------------------------------------------------------------------ */

/**
 * Todas as OS do mesmo telefone. O cliente não é tabela no banco — é a
 * agregação por telefone que o servidor faz —, então esta é a única leitura
 * possível do "cadastro" dele, e ela só acontece se a aba for aberta.
 */
function AbaCliente({
  telefone,
  nome,
  osAtual,
}: {
  telefone: string;
  nome: string;
  osAtual: number;
}) {
  const { data, isLoading, error, refetch } = useHistoricoCliente(telefone || undefined);

  if (!telefone) {
    return (
      <p className="py-8 text-center text-sm text-stone-400">
        Esta OS não tem telefone: sem ele não há como agrupar o histórico do cliente.
      </p>
    );
  }

  const ordens = data?.data ?? [];
  const gastoTotal = ordens.reduce((acc, o) => acc + o.totalPagoCentavos, 0);

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Painel rotulo="OS do cliente" texto={String(ordens.length || '—')} classe="text-stone-800" />
        <Painel rotulo="Já pagou (total)" centavos={gastoTotal} classe="text-oliva" />
        <Painel
          rotulo="Telefone"
          texto={formatarTelefoneBR(telefone)}
          classe="text-stone-800 text-base"
        />
      </div>

      {isLoading && <Esqueleto linhas={4} />}
      {error && <ErroNaTela erro={error} aoTentar={() => void refetch()} />}

      {data && ordens.length === 0 && (
        <p className="py-8 text-center text-sm text-stone-400">
          Nenhuma outra OS para este telefone.
        </p>
      )}

      {ordens.length > 0 && (
        <Secao titulo={`Ordens de serviço de ${nome || 'este cliente'}`}>
          <ul className="divide-y divide-creme-200">
            {ordens.map((o) => {
              const tom = tomStatus(o.status);
              return (
                <li
                  key={o.osNumber}
                  className={cx(
                    'flex items-center gap-3 py-2 text-sm',
                    o.osNumber === osAtual && 'rounded-lg bg-white px-2',
                  )}
                >
                  <span className="w-16 shrink-0 font-bold tabular-nums text-stone-600">
                    #{o.osNumber}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-stone-500">
                    {[o.aparelho, o.marca, o.modelo].filter(Boolean).join(' · ') || '—'}
                  </span>
                  <span className="hidden w-20 shrink-0 text-xs tabular-nums text-stone-400 sm:block">
                    {formatarData(o.entrada) || '—'}
                  </span>
                  <span
                    className="hidden w-32 shrink-0 truncate text-xs font-semibold sm:block"
                    style={{ color: tom.texto }}
                  >
                    {o.statusRotulo}
                  </span>
                  <span className="w-20 shrink-0 text-right tabular-nums text-stone-600">
                    {formatarBRL(o.valorOrcadoCentavos)}
                  </span>
                </li>
              );
            })}
          </ul>

          <Link
            to="/clientes/$telefone"
            params={{ telefone }}
            className="mt-3 inline-block text-xs font-semibold text-brand-700 hover:underline"
          >
            Abrir histórico completo do cliente
          </Link>
        </Secao>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Peças reutilizadas                                                  */
/* ------------------------------------------------------------------ */

/**
 * Gaveta com altura animada.
 *
 * `height: auto` não é animável em CSS; o GSAP mede o conteúdo e interpola até
 * o valor real, o que mantém a gaveta correta mesmo quando os campos
 * condicionais de pagamento entram e mudam a altura.
 */
function Gaveta({ aberta, children }: { aberta: boolean; children: React.ReactNode }) {
  const caixa = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = caixa.current;
      if (!el) return;

      const destino = { height: aberta ? 'auto' : 0, opacity: aberta ? 1 : 0 };
      if (movimentoReduzido()) gsap.set(el, destino);
      else gsap.to(el, { ...destino, duration: 0.35, ease: 'power2.inOut', overwrite: true });
    },
    { dependencies: [aberta] },
  );

  return (
    // Fechada, a gaveta continua no DOM (é o que permite animar a altura), e
    // sem `inert` o Tab entraria nos campos invisíveis dela.
    <div ref={caixa} className="h-0 shrink-0 overflow-hidden opacity-0" inert={!aberta}>
      {children}
    </div>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 rounded-xl bg-white p-4 ring-1 ring-creme-200 last:mb-0">
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-stone-400">
        {titulo}
      </h3>
      {children}
    </section>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">
        {rotulo}
      </dt>
      <dd className="mt-0.5 truncate text-sm text-stone-700">{valor}</dd>
    </div>
  );
}

/** Cartão de indicador. Em `centavos`, o número sobe do zero. */
function Painel({
  rotulo,
  centavos,
  texto,
  classe,
}: {
  rotulo: string;
  centavos?: number;
  texto?: string;
  classe: string;
}) {
  return (
    <div className="rounded-xl bg-white px-3 py-2.5 ring-1 ring-creme-200">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">{rotulo}</p>
      <p className={cx('mt-0.5 truncate text-lg font-bold tabular-nums', classe)}>
        {centavos != null ? <Contador valor={centavos} centavos /> : texto}
      </p>
    </div>
  );
}

function Conta({
  rotulo,
  centavos,
  classe,
  forte,
}: {
  rotulo: string;
  centavos: number;
  classe?: string;
  forte?: boolean;
}) {
  return (
    <div
      className={cx(
        'flex items-center justify-between gap-3',
        forte && 'border-t border-creme-200 pt-1.5',
      )}
    >
      <dt className={cx('text-stone-400', forte && 'font-semibold text-stone-500')}>{rotulo}</dt>
      <dd className={cx('tabular-nums', forte ? 'font-bold' : 'font-medium', classe ?? 'text-stone-600')}>
        {formatarBRL(centavos)}
      </dd>
    </div>
  );
}

/** 'Eliane Brazolin' → 'EB'. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return 'OS';
  if (partes.length === 1) return partes[0]!.slice(0, 2);
  return `${partes[0]![0]!}${partes[1]![0]!}`;
}
